import { validateStandardSchema } from '@angular/forms/signals';

import type { FigureEntity } from '@sites/entity-core';
import { figureLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { extendedPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type FigureFormModel = FigureEntity;
export type FigureDataModel = FigureEntity['data'];

/**
 * Signal-form view of the figure widget. Unlike button/video there is **no
 * variant dispatcher**: figure ships zero-block placement only, so a single
 * factory validates against the zero envelope schema (which requires the
 * `type: 'zero'` tag the host factory always stamps).
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Consumers call them to get the `FieldState`, then read `.value()`,
 * `.touched()`, `.dirty()`, `.errors()` as needed. Convenience `setX(v)`
 * methods are sugar over `field().value.set(v)`.
 */
export function createFigureForm(options?: BaseLegacyEntityFormOptions<FigureEntity>) {
    const base = createBaseLegacyEntityForm<FigureEntity>((schemaPath) => {
        validateStandardSchema(schemaPath, figureLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // `_raw` stays typed as `FieldTree<FigureEntity>` so each accessor below
    // resolves to the correct nested `FieldTree<…>`.
    const _raw = base._raw;

    return composeForm(
        base,

        // Envelope — `type` is always `'zero'` for figure; surfaced for parity
        // with the other widget forms (the zero host's union relies on the
        // shared layout surface).
        legacyWidgetEnvelopePlugin(() => _raw),

        // Figure-specific accessors.
        {
            // Shape & fill
            get figure() {
                return _raw.data.figure;
            },
            setFigure: (value: FigureDataModel['figure']) => {
                _raw.data.figure().value.set(value);
            },
            get backgroundColor() {
                return _raw.data.backgroundColor;
            },
            setBackgroundColor: (value: FigureDataModel['backgroundColor']) => {
                _raw.data.backgroundColor().value.set(value);
            },
            get keepProportions() {
                return _raw.data.keepProportions;
            },
            setKeepProportions: (value: boolean) => {
                _raw.data.keepProportions().value.set(value);
            },

            // Border
            get isBorder() {
                return _raw.data.isBorder;
            },
            setIsBorder: (value: boolean) => {
                _raw.data.isBorder().value.set(value);
            },
            get borderWidth() {
                return _raw.data.borderWidth;
            },
            setBorderWidth: (value: FigureDataModel['borderWidth']) => {
                _raw.data.borderWidth().value.set(value);
            },
            get borderColor() {
                return _raw.data.borderColor;
            },
            setBorderColor: (value: FigureDataModel['borderColor']) => {
                _raw.data.borderColor().value.set(value);
            },
            get borderStyle() {
                return _raw.data.borderStyle;
            },
            setBorderStyle: (value: FigureDataModel['borderStyle']) => {
                _raw.data.borderStyle().value.set(value);
            },

            // Shadow
            get isShadow() {
                return _raw.data.isShadow;
            },
            setIsShadow: (value: boolean) => {
                _raw.data.isShadow().value.set(value);
            },
            get shadowSettings() {
                return _raw.data.shadowSettings;
            },
            // `shadowSettings` is a nullish branch — writing through the parent
            // `data` node avoids the missing-key trap for figures persisted
            // before the feature shipped (the child may not have materialised).
            setShadowSettings: (value: FigureDataModel['shadowSettings']) => {
                _raw.data().value.update((data) => ({ ...data, shadowSettings: value }));
            },
        },

        // Shared setting groups.
        animationPlugin(() => _raw.data),
        visibilityPlugin(() => _raw.data),
        extendedPositionPlugin(() => _raw.data)
    );
}

export type FigureForm = ReturnType<typeof createFigureForm>;
/**
 * Alias for the zero-host union (`ZeroWidgetSignalForm`) — figure has no
 * default variant, so its only form *is* the zero form.
 */
export type FigureZeroForm = FigureForm;
